import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getTeasers } from '../api';
import { trackEvent } from '../utils/analytics';

const keyframesStyle = `
  @keyframes pulse {
    0%, 100% { box-shadow: 0 2px 12px rgba(249,115,22,0.4); }
    50% { box-shadow: 0 2px 20px rgba(249,115,22,0.6); }
  }
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
`;

const CATEGORY_CONFIG = {
  school: {
    heading: "🔥 Frequently Asked in Exams",
    subheading: "Questions repeatedly appearing in board exams",
    ctaText: "Practice Now →",
    badgeLabel: "Asked",
    marksLabel: "marks",
    yearLabel: "Year",
    probabilityLabel: "High Exam Probability",
    probabilityShort: "High",
    subjectPrefix: null,
  },
  college: {
    heading: "🔥 Frequently Asked in University Exams",
    subheading: "Questions commonly repeated in semester examinations",
    ctaText: "View Important Questions →",
    badgeLabel: "Repeated",
    marksLabel: "marks",
    yearLabel: "Year",
    probabilityLabel: "High Exam Relevance",
    probabilityShort: "High",
    subjectPrefix: null,
  }
};

const TeaserCard = ({ teaser, config, isPriority }) => {
  const navigate = useNavigate();
  const title = teaser.display_title || teaser.question.replace(/\+/g, ' ').replace(/_/g, ' ');
  const years = teaser.years || [];
  const marks = teaser.marks || 5;
  const frequency = teaser.frequency || 1;
  const isHighFreq = frequency >= 3;

  const handleClick = () => {
    const searchTerm = teaser.question.replace(/\+/g, ' ').replace(/\s+/g, ' ').trim();
    const category = teaser.board.toLowerCase().includes('engineering') || 
                     teaser.subject.toLowerCase().includes('engineering') ? 'college' : 'school';
    trackEvent("teaser_card_clicked", {
      board: teaser.board,
      grade: teaser.grade,
      subject: teaser.subject,
      frequency: teaser.frequency,
      source: "mode_selection"
    });
    navigate(`/questions?category=${category}&board=${teaser.board}&grade=${teaser.grade}&subject=${teaser.subject}&mode=frequent&q=${encodeURIComponent(searchTerm)}`);
  };

  return (
    <motion.div
      style={{
        flex: '0 0 auto',
        width: '320px',
        marginRight: '16px',
        position: 'relative',
        background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.92) 100%)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderRadius: '18px',
        padding: '16px 18px',
        cursor: 'pointer',
        textAlign: 'left',
        border: isPriority 
          ? '1.5px solid rgba(249,115,22,0.4)' 
          : '1px solid rgba(226,232,240,0.8)',
        boxShadow: isPriority 
          ? '0 8px 32px rgba(249,115,22,0.15), 0 2px 8px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.9)'
          : '0 4px 20px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.8)',
        overflow: 'hidden',
      }}
      onClick={handleClick}
      whileHover={{ 
        scale: 1.03,
        boxShadow: '0 12px 40px rgba(99,102,241,0.2), 0 4px 12px rgba(0,0,0,0.1)'
      }}
      whileTap={{ scale: 0.97 }}
      transition={{ duration: 0.2 }}
    >
      {isPriority && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: 'linear-gradient(90deg, #f97316, #fb923c, #f97316)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 2s linear infinite',
          borderRadius: '18px 18px 0 0'
        }} />
      )}
      
      <div style={{
        position: 'absolute',
        right: '-5px',
        bottom: '-10px',
        fontSize: '70px',
        opacity: 0.03,
        pointerEvents: 'none',
        fontWeight: 700,
        lineHeight: 1
      }}>
        {teaser.subject?.charAt(0) || '📚'}
      </div>
      
      <div style={{
        position: 'absolute',
        top: '10px',
        right: '10px',
        background: isHighFreq 
          ? 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)'
          : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
        color: 'white',
        padding: '3px 9px',
        borderRadius: '20px',
        fontSize: '10px',
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        gap: '3px',
        boxShadow: isHighFreq ? '0 2px 10px rgba(249,115,22,0.45)' : '0 2px 6px rgba(99,102,241,0.3)',
        animation: isHighFreq ? 'pulse 2s infinite' : 'none'
      }}>
        🔥 {frequency}x
      </div>
      
      <div style={{
        fontSize: '14px',
        fontWeight: 600,
        color: '#1e293b',
        marginBottom: '8px',
        marginRight: '70px',
        lineHeight: 1.4,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        minHeight: '40px'
      }}>
        {title}
      </div>
      
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
        marginBottom: '10px'
      }}>
        <span style={{
          fontSize: '10px',
          color: '#64748b',
          background: '#f1f5f9',
          padding: '2px 6px',
          borderRadius: '4px'
        }}>
          📚 {frequency}x
        </span>
        {years.length > 0 && (
          <span style={{
            fontSize: '10px',
            color: '#64748b',
            background: '#f1f5f9',
            padding: '2px 6px',
            borderRadius: '4px'
          }}>
            📅 {years.slice(0, 2).join(' · ')}
          </span>
        )}
        <span style={{
          fontSize: '10px',
          color: '#64748b',
          background: '#f1f5f9',
          padding: '2px 6px',
          borderRadius: '4px'
        }}>
          📝 {marks} {config.marksLabel}
        </span>
      </div>
      
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderTop: '1px solid #e2e8f0',
        paddingTop: '10px'
      }}>
        <div style={{
          fontSize: '10px',
          color: '#64748b',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          flexWrap: 'wrap'
        }}>
          <span style={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            padding: '2px 6px',
            borderRadius: '3px',
            fontSize: '9px',
            fontWeight: 600
          }}>
            🟢 {config.probabilityShort}
          </span>
          <span style={{ color: '#94a3b8' }}>•</span>
          <span>{teaser.board}</span>
          <span style={{ color: '#94a3b8' }}>•</span>
          <span>G{teaser.grade}</span>
          <span style={{ color: '#94a3b8' }}>•</span>
          <span>{teaser.subject}</span>
        </div>
        
        <motion.span
          style={{
            background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
            color: 'white',
            padding: '6px 10px',
            borderRadius: '6px',
            fontSize: '11px',
            fontWeight: 600,
            boxShadow: '0 2px 6px rgba(99,102,241,0.3)'
          }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {config.ctaText}
        </motion.span>
      </div>
    </motion.div>
  );
};

const TeaserCarousel = ({ category = 'school', style = {} }) => {
  const [teasers, setTeasers] = useState([]);
  const scrollRef = useRef(null);
  const isPausedRef = useRef(false);

  const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.school;

  useEffect(() => {
    const fetchTeasers = async () => {
      try {
        const data = await getTeasers(8, category);
        setTeasers(data);
      } catch (err) {
        console.error("Error fetching teasers:", err);
      }
    };
    fetchTeasers();
  }, [category]);

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer || teasers.length === 0) return;

    let animationId;
    const speed = 1.5;

    const scroll = () => {
      if (!isPausedRef.current && scrollContainer) {
        scrollContainer.scrollLeft += speed;
        if (scrollContainer.scrollLeft >= scrollContainer.scrollWidth / 2) {
          scrollContainer.scrollLeft = 0;
        }
      }
      animationId = requestAnimationFrame(scroll);
    };

    animationId = requestAnimationFrame(scroll);
    return () => cancelAnimationFrame(animationId);
  }, [teasers]);

  if (teasers.length === 0) return null;

  const expandedTeasers = [...teasers, ...teasers, ...teasers, ...teasers];
  const sortedTeasers = [...teasers].sort((a, b) => (b.frequency || 0) - (a.frequency || 0));
  const topTeaser = sortedTeasers[0];

  return (
    <div style={{ ...style }}>
      <style>{keyframesStyle}</style>
      
      <div style={{
        textAlign: 'center',
        marginBottom: '20px',
        padding: '0 16px'
      }}>
        <h3 style={{ 
          marginBottom: '8px', 
          color: '#4a5568',
          fontSize: '18px',
          fontWeight: 600,
          letterSpacing: '0.5px'
        }}>
          {config.heading}
        </h3>
        <p style={{
          color: '#64748b',
          fontSize: '13px',
          margin: 0
        }}>
          {config.subheading}
        </p>
      </div>
      
      <div 
        ref={scrollRef}
        onMouseEnter={() => { isPausedRef.current = true; }}
        onMouseLeave={() => { isPausedRef.current = false; }}
        onTouchStart={() => { isPausedRef.current = true; }}
        onTouchEnd={() => { isPausedRef.current = false; }}
        style={{
          display: 'flex',
          overflowX: 'auto',
          scrollBehavior: 'smooth',
          scrollSnapType: 'x mandatory',
          padding: '10px 30px',
          gap: '16px',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          maskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)',
          WebkitMaskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)',
        }}
        className="teaser-carousel"
      >
        {expandedTeasers.map((teaser, i) => (
          <div
            key={`${teaser.id}-${i}`}
            style={{
              flex: '0 0 auto',
              scrollSnapAlign: 'center'
            }}
          >
            <TeaserCard 
              teaser={teaser} 
              config={config}
              isFirst={i < teasers.length && teaser.id === topTeaser?.id}
            />
          </div>
        ))}
      </div>
      
      <style>{`
        .teaser-carousel::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default TeaserCarousel;